import React from 'react';
import clsx from 'clsx';
import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';

export type VoiceSliderOption = {
  value: number;
  label: string;
};

type DiscreteSliderProps = {
  options: VoiceSliderOption[];
  value: number;
  onChange: (value: number) => void;
  className?: string;
  label?: string;
  description?: string;
  error?: string;
  id?: string;
};

export const VoiceDiscreteSlider: React.FC<DiscreteSliderProps> = ({
  options,
  value,
  onChange,
  className,
  label,
  description,
  error,
  id
}) => {
  const currentIndex = options.findIndex((option) => option.value === value);

  return (
    <Field data-field-id={id} className={className}>
      {label && <Label>{label}</Label>}
      {description && <Description>{description}</Description>}

      <div className="space-y-4 pt-3 pb-6">
        <div className="relative">
          <div className="relative h-2 rounded-full bg-neutral-200 dark:bg-neutral-700">
            <div
              className="absolute h-2 rounded-full bg-sky-500 transition-all duration-200"
              style={{
                width: `${(currentIndex / (options.length - 1)) * 100}%`
              }}
            />

            {options.map((_, index) =>
              index === 0 || index === options.length - 1 ? null : (
                <div
                  key={index}
                  className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-white dark:bg-neutral-800"
                  style={{
                    left: `${(index / (options.length - 1)) * 100}%`,
                    transform: 'translateX(-50%) translateY(-50%)'
                  }}
                />
              )
            )}

            <div
              className="absolute top-1/2 h-5 w-5 -translate-y-1/2 cursor-pointer rounded-full border-2 border-sky-500 bg-white dark:bg-neutral-800 shadow-lg transition-all duration-200 hover:scale-110"
              style={{
                left: `${(currentIndex / (options.length - 1)) * 100}%`,
                transform: 'translateX(-50%) translateY(-50%)'
              }}
            />

            <input
              type="range"
              min={0}
              max={options.length - 1}
              step={1}
              value={currentIndex}
              onChange={(e) => {
                const newIndex = parseInt(e.target.value, 10);
                onChange(options[newIndex].value);
              }}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
          </div>

          <div className="relative mt-2">
            {options.map((option, index) => {
              const isFirst = index === 0;
              const isLast = index === options.length - 1;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => onChange(option.value)}
                  className={clsx(
                    'absolute max-w-16 rounded text-xs transition-colors',
                    {
                      '-translate-x-1/2 transform text-center': !isFirst && !isLast,
                      'left-0 text-left': isFirst,
                      'right-0 text-right': isLast
                    },
                    currentIndex === index
                      ? 'font-medium text-sky-600 dark:text-sky-400'
                      : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                  )}
                  style={{
                    left: isFirst
                      ? '0%'
                      : isLast
                        ? 'auto'
                        : `${(index / (options.length - 1)) * 100}%`,
                    right: isLast ? '0%' : 'auto'
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  );
};
